import { RutterLedgerAccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

import {
  MAPPING_COLUMN_MIN_WIDTH_PX,
  MAPPING_SELECT_WIDTH_PX,
} from './mappingColumnWidth';
import MappingSelect, { QuickBooksOption } from './MappingSelect';
import { useQuickBooksMappingQueries } from './QuickBooksMappingQueryContext';

// Rutter reports record statuses as lowercase strings, eg "active" or "archived"
const ACTIVE_QBO_STATUS = 'active';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

interface LedgerMappingRow {
  description: string;
  id: RutterLedgerAccountType;
  ledgerType: RutterLedgerAccountType;
  name: string;
}

export default function LedgerTab() {
  const { isLoading, qboLedgerAccounts, saveLedgerMapping } =
    useQuickBooksMappingQueries();
  const { toasterStore } = useStores();

  const mappedAccountsByLedgerType = useMemo(() => {
    return new Map(
      qboLedgerAccounts
        .filter((ledgerAccount) => ledgerAccount.type)
        .map((ledgerAccount) => [ledgerAccount.type, ledgerAccount] as const),
    );
  }, [qboLedgerAccounts]);

  // one-to-one across the two rows: the account already backing the other ledger role
  // is not offered; a row's existing selection stays visible even when inactive
  const optionsForRow = (row: LedgerMappingRow) => {
    return qboLedgerAccounts
      .filter((ledgerAccount) => {
        if (ledgerAccount.type === row.ledgerType) {
          return true;
        }

        return (
          !ledgerAccount.type &&
          ledgerAccount.rutterApiLedgerAccount?.status === ACTIVE_QBO_STATUS
        );
      })
      .map((ledgerAccount) => ({
        id: ledgerAccount.id,
        name: ledgerAccount.rutterApiLedgerAccount?.name ?? '',
      }))
      .sort((optionA, optionB) => optionA.name.localeCompare(optionB.name));
  };

  const rows: LedgerMappingRow[] = [
    {
      description: t(
        `${I18N_PREFIX}.tread_first.ledger_rows.accounts_payable_description`,
      ),
      id: RutterLedgerAccountType.ACCOUNTS_PAYABLE,
      ledgerType: RutterLedgerAccountType.ACCOUNTS_PAYABLE,
      name: t(`${I18N_PREFIX}.grid.accounts_payable`),
    },
    {
      description: t(
        `${I18N_PREFIX}.tread_first.ledger_rows.accounts_receivable_description`,
      ),
      id: RutterLedgerAccountType.ACCOUNTS_RECEIVABLE,
      ledgerType: RutterLedgerAccountType.ACCOUNTS_RECEIVABLE,
      name: t(`${I18N_PREFIX}.grid.accounts_receivable`),
    },
  ];

  const handleSave = async (
    row: LedgerMappingRow,
    option: Nullable<QuickBooksOption>,
  ) => {
    const saved = await saveLedgerMapping(row.ledgerType, option?.id ?? null);

    if (saved) {
      toasterStore.push(
        alert(
          t(`${I18N_PREFIX}.grid.tread_name_updated`, { name: row.name }),
          AlertTypes.success,
        ),
      );
    }

    return saved;
  };

  const columns: Array<DataTableColumn<LedgerMappingRow>> = [
    {
      field: 'name',
      headerName: t(`${I18N_PREFIX}.grid.name`),
      valueGetter: (row) => row.name,
    },
    {
      field: 'description',
      headerName: t(`${I18N_PREFIX}.tread_first.columns.applies_to`),
      valueGetter: (row) => row.description,
    },
    {
      field: 'mapping',
      headerName: t(`${I18N_PREFIX}.tread_first.columns.quick_books_account`),
      minWidth: MAPPING_COLUMN_MIN_WIDTH_PX,
      renderCell: (row) => {
        const mappedAccount = mappedAccountsByLedgerType.get(row.ledgerType);

        return (
          <MappingSelect
            dataTestId={`quick-books-mapping-select-${row.ledgerType}`}
            onSave={(option) => handleSave(row, option)}
            options={optionsForRow(row)}
            width={MAPPING_SELECT_WIDTH_PX}
            value={
              mappedAccount
                ? {
                    id: mappedAccount.id,
                    name: mappedAccount.rutterApiLedgerAccount?.name ?? '',
                  }
                : null
            }
          />
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      hideToolbar
      id="quick-books-mapping-ledger-grid"
      loading={isLoading}
      rows={rows}
    />
  );
}
